// New-message notification sound, synthesised with the Web Audio API so there
// is no asset to ship, cache-bust, or fail to load.
//
// Two things make a naive `new AudioContext()` ring silently in production:
//
//  1. Autoplay policy — a context constructed before the user has interacted
//     with the page starts in the `suspended` state and STAYS there. Nothing
//     resumes it for you, so every later `start()` is scheduled onto a clock
//     that never advances and no sound is ever heard. We both unlock on the
//     first real gesture and `resume()` defensively before each ring.
//  2. Bursts — a sync run or a rapid back-and-forth would stack a dozen
//     overlapping beeps. Rings inside COALESCE_MS collapse into one.

const COALESCE_MS = 800;

let audioCtx: AudioContext | null = null;
let lastPlayedAt = 0;

function getCtx(): AudioContext | null {
  if (audioCtx) return audioCtx;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  try {
    audioCtx = new Ctor();
  } catch {
    return null;
  }
  return audioCtx;
}

// Unlock on the first user gesture of the session. Once a context has been
// resumed inside a gesture it stays usable for the rest of the page's life,
// so later socket-driven rings (which are NOT gestures) are allowed through.
if (typeof window !== "undefined") {
  const unlock = () => {
    const ctx = getCtx();
    if (ctx && ctx.state === "suspended") void ctx.resume().catch(() => {});
  };
  const opts = { once: true, capture: true, passive: true } as const;
  window.addEventListener("pointerdown", unlock, opts);
  window.addEventListener("keydown", unlock, opts);
  window.addEventListener("touchstart", unlock, opts);
}

/** One soft-attack sine blip. The ramps avoid the click a hard gate makes. */
function blip(ctx: AudioContext, freq: number, at: number, dur: number) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, at);

  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(0.32, at + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(at);
  osc.stop(at + dur + 0.02);
  osc.onended = () => {
    osc.disconnect();
    gain.disconnect();
  };
}

function ring(ctx: AudioContext) {
  const t0 = ctx.currentTime + 0.01;
  blip(ctx, 880, t0, 0.16); // A5
  blip(ctx, 1174.66, t0 + 0.12, 0.28); // D6 — the "ding-dong" resolve
}

/** Play the new-message tone. Safe to call from any socket handler. */
export function playChatSound() {
  const now = Date.now();
  if (now - lastPlayedAt < COALESCE_MS) return;

  const ctx = getCtx();
  if (!ctx) return;
  lastPlayedAt = now;

  try {
    if (ctx.state === "suspended") {
      // Still locked (no gesture yet, or the tab was backgrounded long enough
      // for the browser to suspend us). Resume, then ring once it's running.
      void ctx
        .resume()
        .then(() => ring(ctx))
        .catch(() => {
          // Blocked — don't let the coalesce window swallow the next ring.
          lastPlayedAt = 0;
        });
      return;
    }
    ring(ctx);
  } catch {
    lastPlayedAt = 0;
  }
}
